import { useCallback, useEffect, useState } from 'react';
import { User, UserInput, UserResponse } from '@/types/schemas/user.type';
import api from '@/lib/api';

interface UseUserReturn {
  users: User[];
  currentUser: User | null;
  isLoading: boolean;
  error: Error | null;
  addUser: (newUser: UserInput) => Promise<User>;
  updateUser: (id: number, updatedUser: UserInput) => Promise<User>;
  deleteUser: (id: number) => Promise<void>;
  refreshUsers: () => Promise<void>;
  getCurrentUser: () => Promise<User | null>;
  getUserById: (id: number) => Promise<User | null>;
  getUserByUsername: (username: string) => Promise<User | null>;
  searchUsers: (query: string) => Promise<User[]>;
}

export const useUser = (): UseUserReturn => {
  const [users, setUsers] = useState<User[]>([]);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get<UserResponse>('/users/');
      setUsers(response.data.results);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch users';
      setError(new Error(errorMessage));
      console.error('Error fetching users:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getCurrentUser = useCallback(async (): Promise<User | null> => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get<User>('/users/me/');
      setCurrentUser(response.data);
      return response.data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch current user';
      setError(new Error(errorMessage));
      console.error('Error fetching current user:', err);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const addUser = async (newUser: UserInput): Promise<User> => {
    try {
      const response = await api.post<User>('/users/', newUser);
      setUsers(prev => [...prev, response.data]);
      return response.data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add user';
      setError(new Error(errorMessage));
      console.error('Error adding user:', err);
      throw err;
    }
  };

  const updateUser = async (id: number, updatedUser: UserInput): Promise<User> => {
    try {
      const response = await api.put<User>(`/users/${id}/`, updatedUser);
      setUsers(prev => prev.map(user => (user.id === id ? response.data : user)));

      // Update current user if it's the same user
      if (currentUser?.id === id) {
        setCurrentUser(response.data);
      }

      return response.data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update user';
      setError(new Error(errorMessage));
      console.error('Error updating user:', err);
      throw err;
    }
  };

  const deleteUser = async (id: number): Promise<void> => {
    try {
      await api.delete(`/users/${id}/`);
      setUsers(prev => prev.filter(user => user.id !== id));

      // Clear current user if it's the same user
      if (currentUser?.id === id) {
        setCurrentUser(null);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete user';
      setError(new Error(errorMessage));
      console.error('Error deleting user:', err);
      throw err;
    }
  };

  const getUserById = async (id: number): Promise<User | null> => {
    try {
      // First check if user exists in local state
      const existingUser = users.find(user => user.id === id);
      if (existingUser) {
        return existingUser;
      }

      // If not found locally, fetch from API
      const response = await api.get<User>(`/users/${id}/`);
      return response.data;
    } catch (err) {
      console.error('Error fetching user by ID:', err);
      return null;
    }
  };

  const getUserByUsername = async (username: string): Promise<User | null> => {
    try {
      // First check if user exists in local state
      const existingUser = users.find(user => user.username === username);
      if (existingUser) {
        return existingUser;
      }

      // If not found locally, fetch from API
      const response = await api.get<UserResponse>(`/users/?username=${username}`);
      return response.data.results.length > 0 ? response.data.results[0] : null;
    } catch (err) {
      console.error('Error fetching user by username:', err);
      return null;
    }
  };

  const searchUsers = async (query: string): Promise<User[]> => {
    try {
      const response = await api.get<UserResponse>(`/users/?search=${query}`);
      return response.data.results;
    } catch (err) {
      console.error('Error searching users:', err);
      return [];
    }
  };

  const refreshUsers = useCallback(async () => {
    await fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return {
    users,
    currentUser,
    isLoading,
    error,
    addUser,
    updateUser,
    deleteUser,
    refreshUsers,
    getCurrentUser,
    getUserById,
    getUserByUsername,
    searchUsers
  };
};
